import { PosCartShopFeeRow } from '@/components/fees/PosCartShopFeeRow'
import { useManualTenderDrawer } from '@/lib/services/cash-drawers/useManualTenderDrawer'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAppForm } from '@/lib/form'
import {
  cn,
  formatCurrency,
  generateIdempotencyKey,
  getErrorMessage,
  normalizePhoneDigits,
  normalizeCurrencyValue,
  parseCurrency,
} from '@/lib/utils'
import { getCustomerReceivablesContext } from '@/lib/pos/customer-receivables'
import { getLineItemDisplay } from '@/lib/line-item-display'
import { hasPositiveFsnCartProductValue } from '@/lib/services/fsn-network/fsn-network.utils'
import {
  getOrderQuoteFinancials,
  getOrderQuoteDeliveryControls,
  getOrderQuoteFulfillmentType,
  getOrderQuoteLockState,
  getOrderQuoteRelayFeeControls,
  getOrderQuoteReceivablesBalance,
  getOrderQuoteSubscriptionPaymentBlocker,
  hasPendingOrderQuoteTotals,
  orderQuoteHasSubscription,
  warnMissingOrderQuoteFinancials,
} from '@/lib/order-quote'
import dayjs from '@/lib/dayjs'
import {
  AlertTriangle,
  CreditCard,
  FileClock,
  Loader2,
  Pencil,
  ShoppingBasket,
  ShoppingCart,
  Store,
  Truck,
} from 'lucide-react'
import {
  useDeleteCartMutation,
  useFinalizeCartSalesOrderMutation,
  useIsCartMutating,
  useIsCartSaving,
  useRefundPartialPaymentsMutation,
  useUpdateCartMutation,
} from '@/lib/services/carts/carts.query'
import { useNavigate } from '@tanstack/react-router'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { toast } from 'sonner'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { getPosCartLeaveAction } from '@/lib/pos/cart-leave'
import { getPosCartQuantityByVariantId } from '@/lib/pos/inventory'
import { getOrderChangeRows } from '@/lib/pos/order-change'
import { RefundedOrderItemRow } from './RefundedOrderItemRow'
import { RemovedOrderItemRow } from './RemovedOrderItemRow'
import {
  loadLocalPrintProfile,
  localPrintNeedsPrinterCheck,
  refreshLocalPrintWorkstationProfile,
} from '@/lib/local-print'
import { processPendingDirectPrintJobs } from '@/lib/local-print-direct-jobs'
import { localPrintToastOptions } from '@/lib/local-print-ui'
import { usePathHelper } from '@/hooks/usePathHelper'
import { CustomerSection } from './CustomerSection'
import { CartItemRow } from './CartItemRow'
import { POSCartDiscount } from './POSCartDiscount'
import { DiscardOrderChangeButton } from './DiscardOrderChangeButton'
import { PayLaterConfirmationDialog } from './PayLaterConfirmationDialog'
import { PosOrderChangeSummary } from './PosOrderChangeSummary'
import { SavePosCartDialog, SavedPosCartSummary } from './PosCartSaveControls'
import { PickupModal } from './PickupModal'
import { DeliveryModal } from './DeliveryModal'
import { CartItemDetailsModal, type CartItemDetailsFormValues } from './CustomItemModal'
import {
  buildCartRefundPaymentSignature,
  extractPendingCartRefundIdempotencyKey,
} from './partial-payment-refund-utils'
import { PersonalizationSection } from './PersonalizationSection'
import { usePosCartPaymentSynchronization } from './usePosCartPaymentSynchronization'
import { useWarmTerminals } from '@/lib/services/terminals/terminals.query'
import { canReachCardReaders } from '@/lib/pos/reader-payments'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { getCashDrawerOptionLabel } from '@/lib/services/cash-drawers/cash-drawers.utils'
import { useAbility } from '@/lib/casl'
import {
  getPaymentModalLoadState,
  PaymentModal,
  preloadPaymentModal,
  waitForPaymentModalCode,
} from './payment-modal-loader'
import { PaymentModalLoadFallback } from './PaymentModalLoadFallback'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import { hasUnsettledAttempt } from './payment-modal/pay-now-machine'
import { startPaymentInitializationWatch } from './payment-modal/payment-modal-telemetry'
import { PosOrderCompletionFlow } from './PosOrderCompletionFlow'
import { CashDrawerStatusError } from './CashDrawerStatusError'
import {
  CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE,
  cashDrawerQueriesAreFetching,
  refreshCashDrawerSession,
  refreshManualTenderDrawer,
  retryFailedCashDrawerQueries,
} from '@/lib/services/cash-drawers/cash-drawers.preflight'
import type {
  CreatedCart,
  FinalizeCartSalesOrderInput,
  PosCart,
  PosCartItem,
} from '@/lib/services/carts/carts.types'
import type { ManualTenderDrawer } from '@/lib/services/cash-drawers/cash-drawers.preflight'
import type { FulfillmentTypeValue } from '@/lib/enums/FulfillmentType'
import type { Location } from '@/lib/services/locations/locations.types'
import type { DuplicateSalesOrderReviewFulfillmentType } from '@/lib/services/sales-orders/sales-order.schema'
import type { PaymentInitializationWatch } from './payment-modal/payment-modal-telemetry'
import type { CartSalesOrderHandoff } from './payment-modal/usePaymentSessionController'
import type { PaymentModalProps } from './PaymentModal'

type UpdateCartMutation = ReturnType<typeof useUpdateCartMutation>

/** What PickupModal confirms. */
interface PickupConfirmation {
  date: string
  preferredWeekday?: string
  recipientName: string
  recipientPhone: string
  recipientEmail: string
  specialInstructions: string
}

/** What DeliveryModal confirms: the cart it validated the address against, when it had one, and where it goes. */
interface DeliveryConfirmation extends PickupConfirmation {
  cartId: number | null
  deliveryAddress: Record<string, unknown>
}

function getItemDetailsModalValuesFromCartItem(item: PosCartItem): CartItemDetailsFormValues {
  const display = getLineItemDisplay(item)
  const snapshot = display.snapshot ?? {}
  const media = Array.isArray(snapshot.media) ? snapshot.media[0] : snapshot.media
  // The form edits a photo saved as media, which has an id.
  const uploadedImage = media?.id ? { ...media, id: media.id } : null

  return {
    name: display.primaryName === 'Custom Item' ? '' : display.primaryName,
    description: display.description ?? '',
    notes: display.notes ?? '',
    recipe: display.recipe ?? '',
    image: uploadedImage,
    // A line's price is a decimal string, whether the server or an optimistic line set it.
    price: String(snapshot.price ?? item.price),
    category_id: snapshot.category_id ?? null,
    category_name: snapshot.category_name ?? null,
  }
}

function NetworkDeliveryIndicator() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label="Outside normal delivery area"
          className="inline-flex size-4 shrink-0 items-center justify-center text-interaction"
        >
          <AlertTriangle className="size-4" />
        </span>
      </TooltipTrigger>
      <TooltipContent>
        Outside your normal delivery area. Use Flower Shop Network for fulfillment.
      </TooltipContent>
    </Tooltip>
  )
}

function getFsnSubmitBlocker(
  cart: PosCart | null,
  { allowMissingCardDetails = false }: { allowMissingCardDetails?: boolean } = {}
): string | null {
  const address = cart?.delivery_address

  if (!Array.isArray(cart?.cart_items) || cart.cart_items.length === 0) {
    return 'Add an item first.'
  }

  if (!hasPositiveFsnCartProductValue(cart)) {
    return 'Add an item above $0 first.'
  }

  if (!cart?.fulfillment_due_date) {
    return 'Add a delivery date first.'
  }

  if (!String(cart?.recipient_name ?? '').trim()) {
    return 'Add a recipient name first.'
  }

  if (normalizePhoneDigits(cart?.recipient_phone).length !== 10) {
    return 'Add a 10-digit recipient phone first.'
  }

  if (
    !allowMissingCardDetails &&
    !String(cart?.card_message ?? '').trim() &&
    !String(cart?.signature ?? '').trim()
  ) {
    return 'Add a card message or signature first.'
  }

  if (
    !String(address?.line1 ?? '').trim() ||
    !String(address?.city ?? '').trim() ||
    !String(address?.state ?? '').trim() ||
    !String(address?.postal_code ?? '').trim()
  ) {
    return 'Add the delivery address first.'
  }

  return null
}

interface FsnAutoSubmitOptionProps {
  cart: PosCart | null
  disabled: boolean
  blocker: string | null
  onChange: (submitToFsnNetwork: boolean) => void
}

function FsnAutoSubmitOption({ cart, disabled, blocker, onChange }: FsnAutoSubmitOptionProps) {
  const checked = Boolean(cart?.submit_to_fsn_network) && !blocker
  const isDisabled = disabled || Boolean(blocker)

  return (
    <div className="mt-3 rounded-md border border-status-info-border bg-status-info-background p-3">
      <label
        htmlFor="pos-fsn-auto-submit"
        className="flex cursor-pointer items-start gap-3 text-sm"
      >
        <Checkbox
          id="pos-fsn-auto-submit"
          size="lg"
          className="mt-0.5"
          checked={checked}
          disabled={isDisabled}
          onCheckedChange={(nextChecked) => onChange(Boolean(nextChecked))}
        />
        <span className="min-w-0 space-y-1">
          <span className="block font-medium leading-5">
            Auto-send to Flower Shop Network when order is created
          </span>
          <span className="block text-muted-foreground">
            {blocker ?? 'Flower Shop Network will charge your account.'}
          </span>
        </span>
      </label>
    </div>
  )
}

function isValidDeliveryFeeDraft(value: string): boolean {
  const draft = String(value ?? '').trim()

  if (!draft) {
    return false
  }

  return /^\$?\s*(?:\d+(?:,\d{3})*(?:\.\d{0,2})?|\.\d{1,2})$/.test(draft)
}

function formatKnownCurrency(value: number | null): string {
  return value == null ? '--' : formatCurrency(value)
}

function deliveryFeeDraftValue(deliveryFee: number | null): string {
  return deliveryFee == null ? '' : deliveryFee.toFixed(2)
}

interface DeliveryFeeOverrideInputProps {
  cartId: number | undefined
  deliveryFee: number | null
  hasDeliveryFeeOverride: boolean
  isDisabled: boolean
  updateCartMutation: UpdateCartMutation
}

function DeliveryFeeOverrideInput({
  cartId,
  deliveryFee,
  hasDeliveryFeeOverride,
  isDisabled,
  updateCartMutation,
}: DeliveryFeeOverrideInputProps) {
  const form = useAppForm({
    defaultValues: {
      delivery_fee: deliveryFeeDraftValue(deliveryFee),
    },
  })
  const [isDirty, setIsDirty] = useState(false)
  const rawDraftValueRef = useRef<string | null>(null)

  const resetDraft = () => {
    form.setFieldValue('delivery_fee', deliveryFeeDraftValue(deliveryFee))
    rawDraftValueRef.current = null
    setIsDirty(false)
  }

  const applyDeliveryFee = async () => {
    if (isDisabled || !cartId || !isDirty) {
      return
    }

    const draft = rawDraftValueRef.current ?? form.getFieldValue('delivery_fee')
    rawDraftValueRef.current = null
    const isBlankDraft = String(draft ?? '').trim() === ''

    if (isBlankDraft) {
      if (!hasDeliveryFeeOverride) {
        resetDraft()
        return
      }

      try {
        await updateCartMutation.mutateAsync({
          cartId,
          data: {
            delivery_fee_override: null,
          },
        })
        setIsDirty(false)
      } catch (error) {
        resetDraft()
        toast.error('Failed to update delivery fee', {
          description: getErrorMessage(error) || 'Please try again',
        })
      }

      return
    }

    if (!isValidDeliveryFeeDraft(draft)) {
      resetDraft()
      toast.error('Enter a valid delivery fee', {
        description: 'Use a number like 12.50.',
      })
      return
    }

    const value = Math.max(parseCurrency(draft), 0)
    if (deliveryFee !== null && Math.abs(value - deliveryFee) < 0.005) {
      resetDraft()
      return
    }

    try {
      await updateCartMutation.mutateAsync({
        cartId,
        data: {
          delivery_fee_override: value,
        },
      })
      form.setFieldValue('delivery_fee', value.toFixed(2))
      setIsDirty(false)
    } catch (error) {
      resetDraft()
      toast.error('Failed to update delivery fee', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  return (
    <div className="w-24 shrink-0">
      <form.AppForm>
        <form.AppField name="delivery_fee">
          {(field) => (
            <field.MoneyInput
              id="pos-delivery-fee"
              aria-label="Delivery fee"
              align="right"
              size="sm"
              disabled={isDisabled}
              formatEmptyAsZero={false}
              showFreeBadge={false}
              showFieldInfo={false}
              className="h-8 tabular-nums"
              inputWrapperClassName="w-24"
              onInput={(event) => {
                rawDraftValueRef.current = event.currentTarget.value
                setIsDirty(true)
              }}
              onBlur={() => {
                void applyDeliveryFee()
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === 'Escape') {
                  event.preventDefault()
                  event.currentTarget.blur()
                }
              }}
            />
          )}
        </form.AppField>
      </form.AppForm>
    </div>
  )
}

interface DeliveryFeeOverrideRowProps {
  cart: PosCart | null
  deliveryFee: number | null
  isCartMutating: number
  isCartSaving: number
  updateCartMutation: UpdateCartMutation
  showNetworkDeliveryIndicator: boolean
}

function DeliveryFeeOverrideRow({
  cart,
  deliveryFee: orderQuoteDeliveryFee,
  isCartMutating,
  isCartSaving,
  updateCartMutation,
  showNetworkDeliveryIndicator,
}: DeliveryFeeOverrideRowProps) {
  const deliveryControls = getOrderQuoteDeliveryControls(cart)
  const deliveryFee = deliveryControls.overrideAmount ?? orderQuoteDeliveryFee
  const hasDeliveryFeeOverride = deliveryControls.overridden
  const isDisabled =
    isCartMutating > 0 || !cart?.id || !deliveryControls.editable || updateCartMutation.isPending

  return (
    <div className="flex min-w-0 items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-1.5">
        <Typography variant="label" tone="muted" className="truncate">
          Delivery Fee
        </Typography>
        {showNetworkDeliveryIndicator ? <NetworkDeliveryIndicator /> : null}
      </div>
      {isCartSaving > 0 ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
      ) : (
        <DeliveryFeeOverrideInput
          key={`${cart?.id ?? 'new'}:${deliveryFee == null ? 'unknown' : deliveryFee.toFixed(2)}`}
          cartId={cart?.id}
          deliveryFee={deliveryFee}
          hasDeliveryFeeOverride={hasDeliveryFeeOverride}
          isDisabled={isDisabled}
          updateCartMutation={updateCartMutation}
        />
      )}
    </div>
  )
}

export interface POSCartProps {
  cart: PosCart | null
  /** The location staff chose for this order; the shop's default stands in until they do. */
  location: Location | null
  createCartWithDefaults: () => Promise<CreatedCart>
  defaultLocation: Location | null
  onLockBannerSuppressionChange?: (suppressed: boolean) => void
  onPaymentNavigationBlockingChange?: (blocking: boolean) => void
  onSalesOrderFinalizeStart?: (cart: PosCart | null) => void
  onSalesOrderFinalizeEnd?: () => void
  onSalesOrderNavigationStart?: () => void
  onSalesOrderNavigationEnd?: () => void
  onStartNewOrder?: () => void
  /** An order change's: told when its Pay Now opens or closes. */
  onOrderChangePayNowOpenChange?: (open: boolean) => void
  /** Set on a duplicated order whose past date moved: its pickup or delivery form opens once for staff to review. */
  duplicateReviewMode?: DuplicateSalesOrderReviewFulfillmentType
  onDuplicateReviewOpened?: () => void
  className?: string
}

/**
 * The cart's items scroll above its footer. An order change grows past the footer (removed rows, the refund, the
 * receipts), so the whole column scrolls instead: its items and footer share one scroll area.
 */
function CartColumnScroll({
  wholeColumn,
  children,
}: {
  wholeColumn: boolean
  children: ReactNode
}) {
  // Laid out as a block, as the catalog's scroll areas are: the default table layout lets long lines widen the column.
  return wholeColumn ? (
    <ScrollArea
      className="min-h-0 flex-1"
      viewportClassName="[&>div]:!block [&>div]:!min-w-0 [&>div]:!w-full"
    >
      {children}
    </ScrollArea>
  ) : (
    children
  )
}

function CartItemsScroll({ wholeColumn, children }: { wholeColumn: boolean; children: ReactNode }) {
  // A block too, so a long customer name or email wraps instead of widening the cart past its panel.
  return wholeColumn ? (
    <div>{children}</div>
  ) : (
    <ScrollArea
      className="min-h-0 flex-1"
      viewportClassName="[&>div]:!block [&>div]:!min-w-0 [&>div]:!w-full"
    >
      {children}
    </ScrollArea>
  )
}

export function POSCart({
  cart,
  location,
  createCartWithDefaults,
  defaultLocation,
  onLockBannerSuppressionChange,
  onPaymentNavigationBlockingChange,
  onSalesOrderFinalizeStart,
  onSalesOrderFinalizeEnd,
  onSalesOrderNavigationStart,
  onSalesOrderNavigationEnd,
  onStartNewOrder,
  onOrderChangePayNowOpenChange,
  duplicateReviewMode,
  onDuplicateReviewOpened,
  className,
}: POSCartProps) {
  const navigate = useNavigate()
  const orgId = useRouteOrganizationId()
  const { organization } = useOrganizationContext()
  const path = usePathHelper()

  const deleteCartMutation = useDeleteCartMutation()
  const finalizeCartSalesOrderMutation = useFinalizeCartSalesOrderMutation()
  const updateCartMutation = useUpdateCartMutation()
  const refundPartialPaymentsMutation = useRefundPartialPaymentsMutation()
  const isCartMutating = useIsCartMutating()
  // Save progress only; isCartMutating also covers a quantity still being typed.
  const isCartSaving = useIsCartSaving()
  const pendingCartRefundIdempotencyKey = extractPendingCartRefundIdempotencyKey(cart)
  const cartRefundPaymentSignature = buildCartRefundPaymentSignature(cart)
  const orderQuoteLockState = getOrderQuoteLockState(cart)
  // The server's one lock, which also holds an order change draft while a payment toward it is open on the order.
  const isCartLocked = orderQuoteLockState.locked
  const isPreparedPosPaymentSessionLocked = orderQuoteLockState.isPreparedPosPaymentSessionLock
  // Pay Now picks up a payment the server shows unsettled (a card waiting on the reader, or on its bank), so staff can
  // always open it to check on that payment or cancel it, whatever the cart's lock or balance says.
  const hasPaymentToPickUp = cart ? hasUnsettledAttempt(cart.payment_session) : false
  const isPaymentModalReachable = orderQuoteLockState.paymentModalReachable || hasPaymentToPickUp
  // What Pay Now can collect now, as the cart's payment session says (what's owed less what attempts in flight hold).
  const availableSplitRemainingTotal =
    !cart || cart.payment_session.collectible_total === null
      ? 0
      : parseCurrency(cart.payment_session.collectible_total)
  const orderQuoteFinancials = getOrderQuoteFinancials(cart)
  warnMissingOrderQuoteFinancials(
    cart,
    ['subtotal', 'total', 'paidTotal', 'remainingTotal'],
    'POS cart financials are missing from order_quote.'
  )
  const receiptSubtotal = orderQuoteFinancials.subtotal
  const receiptDiscount = orderQuoteFinancials.adjustmentsTotal ?? 0
  const receiptDeliveryFee = orderQuoteFinancials.deliveryFee
  const receiptTipAmount = orderQuoteFinancials.tipAmount ?? 0
  const receiptRelayFee = orderQuoteFinancials.relayFee ?? 0
  const receiptTotal = orderQuoteFinancials.total
  const receiptRemainingTotal = orderQuoteFinancials.remainingTotal
  const hasKnownFinancialQuote =
    orderQuoteFinancials.hasKnownTotals && receiptTotal !== null && receiptRemainingTotal !== null
  // Receipt amounts include collected shop fees exactly as the server's quote owns them.
  const displayTax = orderQuoteFinancials.customerTax
  const displayPaidTotal = orderQuoteFinancials.customerPaidTotal ?? 0
  const hasCartPayments = displayPaidTotal > 0
  const displayTotal = orderQuoteFinancials.customerTotal
  const quoteFulfillmentType = getOrderQuoteFulfillmentType(cart)
  const resolvedQuoteFulfillmentType = quoteFulfillmentType ?? FulfillmentType.InStore.Value
  // An edit cart changes the items on an issued order; it never takes payment itself.
  const orderChange = cart?.order_change ?? null
  const {
    labelByCartItemId: changeLabelByCartItemId,
    removed: removedOrderItems,
    entries: cartEntries,
    hasChanges: hasOrderChanges,
  } = getOrderChangeRows(cart)
  // An edit cart changes the order's customer, fulfillment, recipient and personal touches as any cart does; the save applies them.
  const areOrderDetailsLocked = isCartLocked
  const quantityByVariantId = getPosCartQuantityByVariantId(
    cart?.cart_items,
    cart?.order_change?.held_units
  )
  const fulfillmentLocation = location ?? defaultLocation ?? null
  const fulfillmentLocationId = Number(fulfillmentLocation?.id ?? cart?.location_id ?? 0) || null
  const ability = useAbility()
  const canOperateCashDrawers = ability?.can('operate', 'cash-drawers') === true
  const {
    statusQuery: cashDrawerStatusQuery,
    trackingEnabled: cashDrawerTrackingEnabled,
    workstationsQuery,
    workstationId,
    pairedWorkstation,
    currentDrawerQuery,
    resolvedDrawer: selectedRefundCashDrawer,
    isLoading: cashDrawerQueriesLoading,
    hasQueryError: cashDrawerQueryError,
    pairingBlocker: refundTillBlocker,
  } = useManualTenderDrawer({
    organizationId: orgId,
    targetLocationId: fulfillmentLocationId,
    canOperateCashDrawers,
  })
  // Cash and checks a refund of the cart's paid tenders hands back, as the server will split it (money rule 4).
  const partialRefundManualTotal = parseCurrency(cart?.refund_preview?.manual_total ?? 0)
  const partialRefundStatusUnavailable = partialRefundManualTotal > 0 && cashDrawerQueryError
  const partialRefundRequiresTill = partialRefundManualTotal > 0 && cashDrawerTrackingEnabled
  const isCashDrawerLoading = partialRefundManualTotal > 0 && cashDrawerQueriesLoading
  const hasSavedCustomer = Boolean(Number(cart?.purchaser_id ?? 0))
  const customerReceivablesOrderBalance = getOrderQuoteReceivablesBalance(cart)
  // What Pay Later would leave on the customer's account, as the server projects it (money rule 4).
  const payLaterProjection = orderChange
    ? (orderChange.quote?.summary ?? null)
    : (cart?.payment_session.pay_later ?? null)
  const customerReceivablesContext = getCustomerReceivablesContext({
    customer: cart?.purchaser,
    projection: payLaterProjection,
  })
  const payLaterAllowed = customerReceivablesContext.payLaterAllowed
  const allowFulfillmentBeforePayment =
    payLaterAllowed && cart?.purchaser?.allow_fulfillment_before_payment === true
  const hasPositivePaymentsAwaitingConfirmation = cart?.refund_preview?.payment_in_flight === true
  const isProjectedOverCreditLimit = customerReceivablesContext.isProjectedOverCreditLimit
  const payLaterBalance = normalizeCurrencyValue(customerReceivablesOrderBalance)
  const hasSubscriptionPackageItems = orderQuoteHasSubscription(cart)
  const subscriptionPaymentBlocker = getOrderQuoteSubscriptionPaymentBlocker(cart)
  const isInStoreFulfillment = resolvedQuoteFulfillmentType === FulfillmentType.InStore.Value
  const inStorePayLaterRequiresPayment =
    isInStoreFulfillment && payLaterAllowed && payLaterBalance > 0 && !allowFulfillmentBeforePayment
  const inStorePayLaterMessage =
    'Collect payment now, or switch this order to Pickup or Delivery before using Pay Later.'
  const cartItemCount = (cart?.cart_items || []).length
  const cartQuantity = (cart?.cart_items || []).reduce((sum, item) => sum + item.quantity, 0)
  const relayFeeControls = getOrderQuoteRelayFeeControls(cart)
  const showNetworkDeliveryIndicator = relayFeeControls.available
  const fsnAutoSubmitBlocker = showNetworkDeliveryIndicator ? getFsnSubmitBlocker(cart) : null
  const fsnManualSubmitBlocker = showNetworkDeliveryIndicator
    ? getFsnSubmitBlocker(cart, { allowMissingCardDetails: true })
    : null
  // Auto-send goes with creating the order. A change's order already exists: the order page's FSN panel sends it.
  const showFsnAutoSubmitOption = showNetworkDeliveryIndicator && !isCartLocked && !orderChange
  // The cart keeps auto-send on while a detail it needs is missing, and the API sends only a ready order, so it's off
  // until the detail is back.
  const isFsnAutoSubmitOn = Boolean(cart?.submit_to_fsn_network) && !fsnAutoSubmitBlocker
  const hasInvalidRelayFee = Boolean(relayFeeControls.enabled && receiptRelayFee <= 0)
  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false)
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false)
  const [deliveryModalCartId, setDeliveryModalCartId] = useState<number | null>(null)
  const deliveryModalKey = isDeliveryModalOpen
    ? 'delivery-active'
    : `delivery-${cart?.id ?? 'new'}-${cart?.updated_at ?? '0'}`
  const [isPayLaterConfirmationOpen, setIsPayLaterConfirmationOpen] = useState(false)
  const [paymentModalMode, setPaymentModalMode] = useState<'split' | null>(null)
  const [isPaymentWorkstationResolving, setIsPaymentWorkstationResolving] = useState(false)
  const paymentModalLoadWatchRef = useRef<PaymentInitializationWatch | null>(null)
  const posPrintWorkstationIdRef = useRef(cart?.organization_workstation_id ?? null)
  const posPrintWorkstationClaimRef = useRef<string | null>(null)
  const paymentModalWarmupKey =
    cart?.id &&
    !orderChange &&
    isPaymentModalReachable &&
    cartItemCount > 0 &&
    availableSplitRemainingTotal > 0 &&
    !subscriptionPaymentBlocker &&
    !hasInvalidRelayFee
      ? [cart.id, availableSplitRemainingTotal, cart.currency_type ?? 'usd'].join(':')
      : null
  const [completedSalesOrderId, setCompletedSalesOrderId] = useState<number | null>(null)
  const [completedSalesOrderOrgId, setCompletedSalesOrderOrgId] = useState<number | null>(null)
  const [canSendCompletedSalesOrderToFsn, setCanSendCompletedSalesOrderToFsn] = useState(false)
  const [completedSalesOrderRequiresFsnCardDetails, setCompletedSalesOrderRequiresFsnCardDetails] =
    useState(false)
  const [isViewingCompletedSalesOrder, setIsViewingCompletedSalesOrder] = useState(false)
  const [editingCartItem, setEditingCartItem] = useState<PosCartItem | null>(null)
  const [isCashDrawerAdmissionPending, setIsCashDrawerAdmissionPending] = useState(false)
  const [refundPartialPaymentsState, setRefundPartialPaymentsState] = useState(() => ({
    idempotencyKey: pendingCartRefundIdempotencyKey,
    paymentSignature: cartRefundPaymentSignature,
  }))
  const cachedRefundPartialPaymentsIdempotencyKey =
    refundPartialPaymentsState.paymentSignature === cartRefundPaymentSignature
      ? refundPartialPaymentsState.idempotencyKey
      : null

  // The shop's reader list is warmed on arrival, before a cart exists, so Pay Now opens on the reader;
  // sales demos can't reach readers (the API answers 403).
  useWarmTerminals({ staleTime: 60_000, enabled: canReachCardReaders(organization) })

  usePosCartPaymentSynchronization(cart, {
    suspend: Boolean(paymentModalMode),
  })

  useEffect(() => {
    if (!paymentModalWarmupKey || paymentModalMode) {
      return
    }

    let idleId: number | null = null

    // Keep checkout initialization off the cart mutation path, then warm it once the total settles.
    const delayId = globalThis.setTimeout(() => {
      const warmPaymentModal = () => {
        void preloadPaymentModal({ paymentState: cart }).catch(() => {
          // This is optional. A later hover or click gets a fresh import and Stripe attempt.
        })
      }

      if (typeof globalThis.requestIdleCallback === 'function') {
        idleId = globalThis.requestIdleCallback(warmPaymentModal, {
          timeout: 1_000,
        })
      } else {
        warmPaymentModal()
      }
    }, 500)

    return () => {
      globalThis.clearTimeout(delayId)

      if (idleId !== null && typeof globalThis.cancelIdleCallback === 'function') {
        globalThis.cancelIdleCallback(idleId)
      }
    }
  }, [cart, paymentModalMode, paymentModalWarmupKey])

  const recordPaymentModalInitializationStage = useCallback<
    NonNullable<PaymentModalProps['onInitializationStage']>
  >((stage) => {
    const watch = paymentModalLoadWatchRef.current
    if (!watch) {
      return
    }

    watch.mark(stage)

    if (stage === 'payment_modal_rendered') {
      watch.complete()
      paymentModalLoadWatchRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      paymentModalLoadWatchRef.current?.cancel()
      paymentModalLoadWatchRef.current = null
    }
  }, [])

  const openDeliveryModal = async () => {
    try {
      let modalCartId = cart?.id ?? null

      if (!modalCartId && fulfillmentLocation?.is_delivery_cutoff_overrides_enabled) {
        const newCart = await createCartWithDefaults()
        modalCartId = newCart?.id ?? null

        if (!modalCartId) {
          throw new Error('Please try again')
        }
      }

      setDeliveryModalCartId(modalCartId)
      setIsDeliveryModalOpen(true)
    } catch (error) {
      toast.error('Unable to start delivery', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  const handleDeliveryModalOpenChange = (isOpen: boolean) => {
    setIsDeliveryModalOpen(isOpen)

    if (!isOpen) {
      setDeliveryModalCartId(null)
    }
  }

  // A duplicated order whose past date moved opens its pickup or delivery form once its cart has loaded, so staff
  // confirm the new date. Once per cart: closing the form doesn't bring it back.
  const [reviewedDuplicateCartId, setReviewedDuplicateCartId] = useState<number | null>(null)
  if (duplicateReviewMode && cart?.id && reviewedDuplicateCartId !== cart.id) {
    setReviewedDuplicateCartId(cart.id)
    if (duplicateReviewMode === FulfillmentType.Pickup.Value) {
      setIsPickupModalOpen(true)
    } else {
      setDeliveryModalCartId(cart.id)
      setIsDeliveryModalOpen(true)
    }
  }
  // The route keeps the review flag in its URL, so clearing it once the form opens is a navigation.
  const clearDuplicateReview = useEffectEvent(() => onDuplicateReviewOpened?.())
  useEffect(() => {
    if (reviewedDuplicateCartId !== null) {
      clearDuplicateReview()
    }
  }, [reviewedDuplicateCartId])

  const handleFulfillmentChange = async (type: FulfillmentTypeValue) => {
    if (type === FulfillmentType.InStore.Value) {
      // A cart that fails to be created reports like a failed update, rather than doing nothing.
      try {
        const currentCartId = cart?.id ?? (await createCartWithDefaults()).id
        await updateCartMutation.mutateAsync({
          cartId: currentCartId,
          data: {
            ...(fulfillmentLocationId ? { location_id: fulfillmentLocationId } : {}),
            fulfillment_type: type,
            fulfillment_due_date: null,
            special_instructions: null,
            recipient_name: null,
            recipient_phone: null,
            recipient_email: null,
          },
        })
      } catch (error) {
        toast.error('Failed to update cart', {
          description: getErrorMessage(error) || 'Please try again',
        })
      }
      return
    }

    if (type === FulfillmentType.Pickup.Value) {
      setIsPickupModalOpen(true)
    } else if (type === FulfillmentType.Delivery.Value) {
      await openDeliveryModal()
    }
  }

  const handlePickupConfirm = async (data: PickupConfirmation) => {
    let currentCartId = cart?.id
    if (!currentCartId) {
      const newCart = await createCartWithDefaults()
      currentCartId = newCart.id
    }
    if (currentCartId) {
      try {
        await updateCartMutation.mutateAsync({
          cartId: currentCartId,
          data: {
            ...(fulfillmentLocationId ? { location_id: fulfillmentLocationId } : {}),
            fulfillment_type: FulfillmentType.Pickup.Value,
            fulfillment_due_date: data.date,
            ...(data.preferredWeekday
              ? { subscription_preferred_weekday: data.preferredWeekday }
              : {}),
            special_instructions: data.specialInstructions,
            recipient_name: data.recipientName,
            recipient_phone: data.recipientPhone,
            recipient_email: data.recipientEmail,
          },
        })
      } catch (error) {
        toast.error('Failed to update cart', {
          description: getErrorMessage(error) || 'Please try again',
        })
      }
    }
  }

  const handleDeliveryConfirm = async (data: DeliveryConfirmation) => {
    let currentCartId = data.cartId ?? cart?.id
    if (!currentCartId) {
      const newCart = await createCartWithDefaults()
      currentCartId = newCart.id
    }
    if (currentCartId) {
      const updateData = {
        ...(fulfillmentLocationId ? { location_id: fulfillmentLocationId } : {}),
        fulfillment_type: FulfillmentType.Delivery.Value,
        fulfillment_due_date: data.date,
        ...(data.preferredWeekday ? { subscription_preferred_weekday: data.preferredWeekday } : {}),
        delivery_address: data.deliveryAddress,
        special_instructions: data.specialInstructions,
        recipient_name: data.recipientName,
        recipient_phone: data.recipientPhone,
        recipient_email: data.recipientEmail,
      }

      try {
        await updateCartMutation.mutateAsync({
          cartId: currentCartId,
          data: updateData,
        })
      } catch (error) {
        toast.error('Failed to update cart', {
          description: getErrorMessage(error) || 'Please try again',
        })
      }
    }
  }

  const handleFsnAutoSubmitChange = async (submitToFsnNetwork: boolean) => {
    if (!cart?.id) {
      return
    }

    try {
      await updateCartMutation.mutateAsync({
        cartId: cart.id,
        data: {
          submit_to_fsn_network: submitToFsnNetwork,
        },
      })
    } catch (error) {
      toast.error('Failed to update Flower Shop Network option', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  const invalidRelayFeePayLaterMessage = 'Enter a Relay Fee greater than $0 before using Pay Later.'

  const hasPendingCartRefundTotals = hasPendingOrderQuoteTotals(cart)
  // What's left to pay, once the quote knows both what's paid and what's left.
  const knownCartRefundRemaining =
    orderQuoteFinancials.paidTotal !== null ? orderQuoteFinancials.remainingTotal : null
  const hasRefundablePaidCartTender = (cart?.refund_preview?.payments.length ?? 0) > 0
  const canRefundPartialPayments =
    hasRefundablePaidCartTender &&
    !hasPositivePaymentsAwaitingConfirmation &&
    (orderQuoteLockState.canRefundPayments ||
      (!isCartLocked &&
        (knownCartRefundRemaining !== null
          ? (orderQuoteFinancials.paidTotal ?? 0) > 0 && knownCartRefundRemaining > 0
          : hasPendingCartRefundTotals)))

  const resolveCurrentWorkstation = async () => {
    if (!cart?.id) {
      posPrintWorkstationIdRef.current = null
      posPrintWorkstationClaimRef.current = null
      return { id: null, claim: null }
    }

    let workstation = null
    try {
      workstation = (await refreshLocalPrintWorkstationProfile(cart.organization_id))?.workstation
    } catch {
      // A temporarily unavailable bridge falls through to the signed cached identity below.
    }

    // Bridge availability must not decide whether the API creates a durable local print job.
    // The API revalidates this signed identity against current workstation settings.
    const liveIdentity =
      workstation?.id && workstation.checkout_claim
        ? { id: workstation.id, claim: workstation.checkout_claim }
        : null
    const cachedProfile = liveIdentity ? null : loadLocalPrintProfile(cart.organization_id)
    const workstationId = liveIdentity ? liveIdentity.id : cachedProfile?.workstationId
    const workstationClaim = liveIdentity ? liveIdentity.claim : cachedProfile?.workstationClaim
    const identity =
      workstationId && workstationClaim
        ? { id: workstationId, claim: workstationClaim }
        : { id: null, claim: null }
    posPrintWorkstationIdRef.current = identity.id
    posPrintWorkstationClaimRef.current = identity.claim
    return identity
  }

  // Pay Now opens once the print workstation is resolved and its code has loaded (for a few seconds at most).
  const prepareToShowPaymentModal = () =>
    Promise.all([resolveCurrentWorkstation(), waitForPaymentModalCode()])

  const openPaymentModal = async () => {
    if (isPaymentWorkstationResolving) return

    setIsPaymentWorkstationResolving(true)
    try {
      await prepareToShowPaymentModal()
    } finally {
      setIsPaymentWorkstationResolving(false)
    }

    showPaymentModal('pay_now_clicked')
  }
  /** Shows Pay Now once the print workstation is resolved and its code has loaded. */
  const showPaymentModal = (initializationStage: string) => {
    paymentModalLoadWatchRef.current?.cancel()

    const moduleLoadState = getPaymentModalLoadState()
    const watch = startPaymentInitializationWatch({
      initializationKind: 'payment_modal',
      initializationStage,
      paymentSurface: 'pos_pay_now',
      paymentMethod: 'not_selected',
      phase: 'entry',
      organizationId: cart?.organization_id ?? null,
      cartId: cart?.id ?? null,
      paymentModalModuleLoadElapsedMs: moduleLoadState.elapsedMs,
    })

    watch.mark(`payment_modal_module_${moduleLoadState.status}`)
    paymentModalLoadWatchRef.current = watch
    setPaymentModalMode('split')
  }
  const cancelPaymentModalLoad = () => {
    paymentModalLoadWatchRef.current?.cancel()
    paymentModalLoadWatchRef.current = null
    setPaymentModalMode(null)
  }
  // Arriving on a cart whose payment is still waiting (the page reloaded, or staff came back to it while the customer
  // was at the reader), Pay Now opens on that payment by itself, as it was before the page went away. Checked once per
  // cart as it arrives, so closing Pay Now later doesn't bring it back.
  const [arrival, setArrival] = useState<{
    cartId: number
    waitingPaymentId: number | null
    resumed: boolean
  } | null>(null)
  if (cart && arrival?.cartId !== cart.id) {
    setArrival({
      cartId: cart.id,
      waitingPaymentId: hasUnsettledAttempt(cart.payment_session)
        ? (cart.payment_session.attempt?.payment_id ?? null)
        : null,
      resumed: false,
    })
    // Arriving on a cart already converted to an order (reopened later, or the page reloaded after the sale), the POS
    // shows the sale's completion again: staff leave it by viewing the order or starting a new one, as after a sale.
    if (cart.sales_order_id && orderQuoteLockState.isFinalizedSale && !cart.order_change) {
      setCompletedSalesOrderId(cart.sales_order_id)
      setCompletedSalesOrderOrgId(cart.organization_id)
    }
  }
  const waitingPaymentOnArrival = arrival?.waitingPaymentId ?? null
  const isResumingWaitingPayment = waitingPaymentOnArrival !== null && !arrival?.resumed
  // The Pay Now button is busy until Pay Now opens on the waiting payment, as it is while a click opens Pay Now.
  const isPaymentModalOpening = isPaymentWorkstationResolving || isResumingWaitingPayment
  const prepareToResumeWaitingPayment = useEffectEvent(prepareToShowPaymentModal)
  const resumeWaitingPayment = useEffectEvent((paymentId: number) => {
    setArrival((current) =>
      current?.waitingPaymentId === paymentId ? { ...current, resumed: true } : current
    )
    showPaymentModal('pay_now_resumed_on_arrival')
  })
  // An effect, because opening resolves the print workstation and loads Pay Now's code first, which can't happen during
  // render. Leaving the cart before that finishes (for another cart, or another page) drops the open.
  useEffect(() => {
    if (waitingPaymentOnArrival === null) {
      return
    }
    let left = false
    void prepareToResumeWaitingPayment().then(() => {
      if (!left) {
        resumeWaitingPayment(waitingPaymentOnArrival)
      }
    })

    return () => {
      left = true
    }
  }, [waitingPaymentOnArrival])
  // A layout effect, so the page's lock banner is gone before the browser paints: while Pay Now opens on a waiting payment
  // by itself, the banner would otherwise flash first.
  useLayoutEffect(() => {
    onLockBannerSuppressionChange?.(
      Boolean(paymentModalMode) || Boolean(completedSalesOrderId) || isResumingWaitingPayment
    )
  }, [
    completedSalesOrderId,
    isResumingWaitingPayment,
    onLockBannerSuppressionChange,
    paymentModalMode,
  ])
  const canCompleteOrder =
    orderQuoteLockState.canCompleteOrder &&
    cartItemCount > 0 &&
    !hasPositivePaymentsAwaitingConfirmation &&
    !hasInvalidRelayFee &&
    !subscriptionPaymentBlocker
  const leaveCartAction = getPosCartLeaveAction(cart)
  const canCreatePayLaterOrder =
    isCartMutating <= 0 &&
    !finalizeCartSalesOrderMutation.isPending &&
    (cart?.cart_items || []).length > 0 &&
    !isCartLocked &&
    hasSavedCustomer &&
    hasKnownFinancialQuote &&
    payLaterAllowed &&
    payLaterBalance > 0 &&
    !hasSubscriptionPackageItems &&
    !hasPositivePaymentsAwaitingConfirmation &&
    !hasInvalidRelayFee &&
    !inStorePayLaterRequiresPayment &&
    !isProjectedOverCreditLimit
  const payLaterUnavailableReason = !hasSavedCustomer
    ? 'Pay Later requires a saved customer on the order.'
    : !hasKnownFinancialQuote
      ? 'Order totals are still calculating.'
      : payLaterBalance <= 0
        ? 'There is no unpaid balance to create as Pay Later.'
        : hasSubscriptionPackageItems
          ? 'Subscriptions must be paid now with a saved card.'
          : !payLaterAllowed
            ? 'Pay Later is turned off for this customer.'
            : hasInvalidRelayFee
              ? invalidRelayFeePayLaterMessage
              : inStorePayLaterRequiresPayment
                ? inStorePayLaterMessage
                : isProjectedOverCreditLimit
                  ? 'Pay Later is unavailable because this order would put the customer over their credit limit.'
                  : null
  const payLaterHelperText = hasPositivePaymentsAwaitingConfirmation
    ? 'Finish confirming the current payment before creating a Pay Later order.'
    : hasInvalidRelayFee
      ? invalidRelayFeePayLaterMessage
      : inStorePayLaterRequiresPayment
        ? inStorePayLaterMessage
        : !payLaterAllowed && hasSavedCustomer
          ? 'Pay Later is turned off for this customer.'
          : null
  const payLaterButton = (
    <Button variant="outline" className="w-full gap-2" disabled={!canCreatePayLaterOrder}>
      <FileClock className="h-4 w-4" />
      Pay Later
    </Button>
  )

  const handleFinalizeSalesOrder = async (
    paymentMode: FinalizeCartSalesOrderInput['paymentMode'],
    options: { allowCreditLimitOverride?: boolean } = {}
  ) => {
    if (!cart?.id) return

    onSalesOrderFinalizeStart?.(cart)

    try {
      onLockBannerSuppressionChange?.(true)

      const organizationWorkstation = await resolveCurrentWorkstation()

      const response = await finalizeCartSalesOrderMutation.mutateAsync({
        cartId: cart.id,
        paymentMode,
        allowCreditLimitOverride: Boolean(options.allowCreditLimitOverride),
        organizationWorkstationId: organizationWorkstation.id,
        organizationWorkstationClaim: organizationWorkstation.claim,
      })
      const salesOrderId = Number(response?.sales_order_id ?? response?.sales_order?.id ?? 0)
      const salesOrderOrgId = Number(
        response?.sales_order?.organization_id ?? cart.organization_id ?? 0
      )

      if (!salesOrderId) {
        throw new Error('Order is still syncing.')
      }

      flushSync(() => {
        setIsPayLaterConfirmationOpen(false)
        setCompletedSalesOrderId(salesOrderId)
        setCompletedSalesOrderOrgId(salesOrderOrgId || null)
        setCanSendCompletedSalesOrderToFsn(
          showNetworkDeliveryIndicator &&
            !fsnManualSubmitBlocker &&
            !isFsnAutoSubmitOn &&
            (paymentMode !== 'pay_later' || allowFulfillmentBeforePayment)
        )
        setCompletedSalesOrderRequiresFsnCardDetails(
          !String(cart?.card_message ?? '').trim() && !String(cart?.signature ?? '').trim()
        )
      })
      void processPendingDirectPrintJobs({
        organizationId: salesOrderOrgId,
        salesOrderId,
      }).catch((printError: unknown) => {
        toast.warning(
          localPrintNeedsPrinterCheck(printError)
            ? 'Sale completed. Check the printer before printing again.'
            : 'Sale completed, but one or more documents did not print.',
          {
            ...localPrintToastOptions(printError, getErrorMessage(printError)),
          }
        )
      })
    } catch (error) {
      onSalesOrderFinalizeEnd?.()
      onLockBannerSuppressionChange?.(false)
      toast.error(
        paymentMode === 'pay_later'
          ? 'Failed to create pay-later order'
          : 'Failed to finalize order',
        {
          description: getErrorMessage(error) || 'Please try again',
        }
      )
    }
  }

  const handleCompletedSalesOrder = ({ salesOrderId, organizationId }: CartSalesOrderHandoff) => {
    const nextSalesOrderId = salesOrderId
    const nextOrganizationId = Number(organizationId)

    if (!nextSalesOrderId) {
      return
    }

    onSalesOrderFinalizeStart?.(cart)
    setCompletedSalesOrderId(nextSalesOrderId)
    setCompletedSalesOrderOrgId(nextOrganizationId || null)
    setCanSendCompletedSalesOrderToFsn(
      showNetworkDeliveryIndicator && !fsnManualSubmitBlocker && !isFsnAutoSubmitOn
    )
    setCompletedSalesOrderRequiresFsnCardDetails(
      !String(cart?.card_message ?? '').trim() && !String(cart?.signature ?? '').trim()
    )
  }

  const handleCompletedSalesOrderOutput = ({
    salesOrderId,
    organizationId,
  }: CartSalesOrderHandoff) => {
    const nextSalesOrderId = salesOrderId
    const nextOrganizationId = Number(organizationId)

    if (!nextSalesOrderId) {
      return
    }

    void processPendingDirectPrintJobs({
      organizationId: nextOrganizationId,
      salesOrderId: nextSalesOrderId,
    }).catch((printError: unknown) => {
      toast.warning(
        localPrintNeedsPrinterCheck(printError)
          ? 'Sale completed. Check the printer before printing again.'
          : 'Sale completed, but one or more documents did not print.',
        {
          ...localPrintToastOptions(printError, getErrorMessage(printError)),
        }
      )
    })
  }

  const leaveCurrentCart = () => {
    if (onStartNewOrder) {
      onStartNewOrder()
      return
    }

    void navigate({
      // '.' is what an omitted `to` resolves to; naming it lets the router type the search.
      to: '.',
      search: (prev) => ({ ...prev, cartId: undefined }),
      ignoreBlocker: true,
      replace: true,
    })
  }

  const handleStartNewOrderAfterCompletion = () => {
    setCompletedSalesOrderId(null)
    setCompletedSalesOrderOrgId(null)
    setCanSendCompletedSalesOrderToFsn(false)
    setCompletedSalesOrderRequiresFsnCardDetails(false)
    setIsViewingCompletedSalesOrder(false)
    onSalesOrderFinalizeEnd?.()
    leaveCurrentCart()
  }

  const handleViewCompletedSalesOrder = async () => {
    if (!completedSalesOrderId) {
      return
    }

    const organizationId = Number(completedSalesOrderOrgId ?? cart?.organization_id ?? 0)

    if (!organizationId) {
      toast.error('Failed to open order', {
        description: 'Start a new order, then open the order from the order list.',
      })
      return
    }

    setIsViewingCompletedSalesOrder(true)
    onSalesOrderNavigationStart?.()

    try {
      await Promise.resolve(
        navigate({
          ...path.salesOrders.show(completedSalesOrderId).using({ orgId: String(organizationId) })
            .link,
          ignoreBlocker: true,
        })
      )
      setCompletedSalesOrderId(null)
      setCompletedSalesOrderOrgId(null)
      setCanSendCompletedSalesOrderToFsn(false)
      setCompletedSalesOrderRequiresFsnCardDetails(false)
    } catch (error) {
      setIsViewingCompletedSalesOrder(false)
      onSalesOrderNavigationEnd?.()
      toast.error('Failed to open order', {
        description: getErrorMessage(error) || 'Please try again',
      })
    }
  }

  const handleRefundPartialPayments = async () => {
    if (!cart?.id) return false

    setIsCashDrawerAdmissionPending(true)
    let drawerContext: ManualTenderDrawer = {
      trackingEnabled: false,
      session: null,
      workstation: null,
    }
    if (partialRefundManualTotal > 0) {
      try {
        drawerContext = await refreshManualTenderDrawer({
          canOperateCashDrawers,
          currentDrawerQuery,
          organizationId: orgId,
          pairedWorkstation,
          statusQuery: cashDrawerStatusQuery,
          targetLocationId: fulfillmentLocationId,
          workstationId,
          workstationsQuery,
        })
      } catch (error) {
        toast.error(
          (error instanceof Error ? error.message : '') ||
            getErrorMessage(error) ||
            CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE
        )
        setIsCashDrawerAdmissionPending(false)
        return false
      }
    }

    const idempotencyKey =
      pendingCartRefundIdempotencyKey ??
      cachedRefundPartialPaymentsIdempotencyKey ??
      generateIdempotencyKey()
    setRefundPartialPaymentsState({
      idempotencyKey,
      paymentSignature: cartRefundPaymentSignature,
    })

    try {
      const response = await refundPartialPaymentsMutation.mutateAsync({
        cartId: cart.id,
        idempotencyKey,
        ...(drawerContext.trackingEnabled ? { cashDrawerSessionId: drawerContext.session.id } : {}),
      })

      const summary = response?.summary || {}
      const succeededTotal = parseCurrency(summary?.succeeded_total)
      const pendingTotal = parseCurrency(summary?.pending_total)
      const failedTotal = parseCurrency(summary?.failed_total)

      if (failedTotal > 0) {
        toast.warning('Partial payment refund completed with issues', {
          description: `Succeeded ${formatCurrency(succeededTotal)}, pending ${formatCurrency(
            pendingTotal
          )}, failed ${formatCurrency(failedTotal)}.`,
        })
      } else if (pendingTotal > 0) {
        toast.success('Partial payment refund submitted', {
          description: `Succeeded ${formatCurrency(succeededTotal)} and ${formatCurrency(
            pendingTotal
          )} is pending confirmation.`,
        })
      } else {
        toast.success('Partial payments refunded successfully')
      }

      if (pendingTotal <= 0) {
        setRefundPartialPaymentsState({
          idempotencyKey: null,
          paymentSignature: cartRefundPaymentSignature,
        })
      }

      return true
    } catch (error) {
      toast.error('Failed to refund partial payments', {
        description: getErrorMessage(error) || 'Please try again',
      })
      if (drawerContext.trackingEnabled) {
        void refreshCashDrawerSession(currentDrawerQuery)
      }
      return false
    } finally {
      setIsCashDrawerAdmissionPending(false)
    }
  }

  return (
    <Card size="sm" className={cn('@container flex flex-col lg:h-full lg:min-h-0', className)}>
      <CardHeader size="sm" className="shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle>{orderChange ? 'Order' : 'Cart'}</CardTitle>
          <div className="flex gap-2 items-center">
            {isCartSaving > 0 && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            {orderChange && cart && orderChange.closed === null ? (
              <DiscardOrderChangeButton
                salesOrderId={orderChange.sales_order_id}
                cartId={cart.id}
                // Until the change is priced, its edits can't be told apart, so it can always be discarded.
                hasChanges={!orderChange.quote || hasOrderChanges}
                canDiscard={orderQuoteLockState.canDelete === true}
              />
            ) : null}
            {orderChange ? null : (
              <>
                <SavePosCartDialog
                  cart={cart}
                  disabled={isCartMutating > 0}
                  onSaved={leaveCurrentCart}
                />
                {leaveCartAction === 'start-new' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={leaveCurrentCart}
                    disabled={!cart || isCartMutating > 0}
                  >
                    Start New Cart
                  </Button>
                ) : null}
                {leaveCartAction === 'clear' ? (
                  <ConfirmationDialog
                    variant="destructive"
                    title="Clear Cart?"
                    description="Are you sure you want to clear the cart? This action cannot be undone."
                    onConfirm={async () => {
                      // Clear Cart is disabled without a cart.
                      if (!cart) {
                        return
                      }
                      try {
                        await deleteCartMutation.mutateAsync(cart.id)
                        leaveCurrentCart()
                      } catch (error) {
                        toast.error('Failed to clear cart', {
                          description: getErrorMessage(error) || 'Please try again',
                        })
                      }
                    }}
                    confirmText="Clear Cart"
                    trigger={
                      <Button variant="outline" size="sm" disabled={!cart || isCartMutating > 0}>
                        Clear Cart
                      </Button>
                    }
                  />
                ) : null}
              </>
            )}
          </div>
        </div>
        {/* The cart shown behind the completion dialog is a pre-completion snapshot. */}
        {completedSalesOrderId || orderChange ? null : <SavedPosCartSummary cart={cart} />}
        {!orderChange && leaveCartAction === 'save-first' ? (
          <Typography variant="label" tone="muted" className="block">
            This order has a payment. Save it as a draft to start a new cart.
          </Typography>
        ) : null}
      </CardHeader>

      <CardContent size="sm" className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CartColumnScroll wholeColumn={Boolean(orderChange)}>
          <CartItemsScroll wholeColumn={Boolean(orderChange)}>
            <div className="space-y-4 pr-4 pb-4 pl-1">
              {/* Customer Section */}
              <CustomerSection
                cart={cart}
                createCartWithDefaults={createCartWithDefaults}
                disabled={areOrderDetailsLocked}
                payLater={payLaterProjection}
                isOrderChange={Boolean(orderChange)}
              />

              {/* Fulfillment Type Selection */}
              <div className="space-y-2">
                <Typography variant="label" tone="muted">
                  Fulfillment
                </Typography>
                {(cart?.fulfillment_type === FulfillmentType.Pickup.Value ||
                  cart?.fulfillment_type === FulfillmentType.Delivery.Value) && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <Typography variant="labelStrong" className="line-clamp-1">
                          <span className="inline-flex min-w-0 items-center gap-1.5">
                            <span className="truncate">
                              {cart?.fulfillment_type === FulfillmentType.Pickup.Value
                                ? FulfillmentType.Pickup.Name
                                : FulfillmentType.Delivery.Name}
                            </span>
                            {showNetworkDeliveryIndicator ? <NetworkDeliveryIndicator /> : null}
                          </span>
                        </Typography>
                        {cart?.fulfillment_due_date && (
                          <Typography variant="label" tone="muted" className="block mt-1">
                            {dayjs(cart.fulfillment_due_date).format('MMM D, YYYY')}
                          </Typography>
                        )}
                        {cart?.fulfillment_type === FulfillmentType.Delivery.Value &&
                          cart?.delivery_address && (
                            <Typography
                              variant="label"
                              tone="muted"
                              className="block mt-1 line-clamp-2"
                            >
                              {[
                                cart.delivery_destination_name,
                                cart.delivery_address.line1,
                                cart.delivery_address.line2,
                                `${cart.delivery_address.city}, ${cart.delivery_address.state} ${cart.delivery_address.postal_code}`,
                              ]
                                .filter(Boolean)
                                .join(', ')}
                            </Typography>
                          )}
                        {showFsnAutoSubmitOption ? (
                          <FsnAutoSubmitOption
                            cart={cart}
                            disabled={isCartMutating > 0 || updateCartMutation.isPending}
                            blocker={fsnAutoSubmitBlocker}
                            onChange={handleFsnAutoSubmitChange}
                          />
                        ) : null}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        onClick={() => {
                          if (cart?.fulfillment_type === FulfillmentType.Pickup.Value) {
                            setIsPickupModalOpen(true)
                          } else if (cart?.fulfillment_type === FulfillmentType.Delivery.Value) {
                            void openDeliveryModal()
                          }
                        }}
                        disabled={isCartMutating > 0 || areOrderDetailsLocked}
                        aria-label="Edit fulfillment details"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
                {
                  <div className="flex flex-col @[20rem]:grid @[20rem]:grid-cols-3 gap-2">
                    <Button
                      variant={
                        (cart?.fulfillment_type || FulfillmentType.InStore.Value) ===
                        FulfillmentType.InStore.Value
                          ? 'default'
                          : 'outline'
                      }
                      size="sm"
                      onClick={() => handleFulfillmentChange(FulfillmentType.InStore.Value)}
                      disabled={isCartMutating > 0 || isCartLocked || hasSubscriptionPackageItems}
                      className="flex items-center gap-2"
                    >
                      <Store className="h-4 w-4" />
                      {FulfillmentType.InStore.Name}
                    </Button>
                    <Button
                      variant={
                        cart?.fulfillment_type === FulfillmentType.Pickup.Value
                          ? 'default'
                          : 'outline'
                      }
                      size="sm"
                      onClick={() => handleFulfillmentChange(FulfillmentType.Pickup.Value)}
                      disabled={isCartMutating > 0 || isCartLocked}
                      className="flex items-center gap-2"
                    >
                      <ShoppingBasket className="h-4 w-4" />
                      {FulfillmentType.Pickup.Name}
                    </Button>
                    <Button
                      variant={
                        cart?.fulfillment_type === FulfillmentType.Delivery.Value
                          ? 'default'
                          : 'outline'
                      }
                      size="sm"
                      onClick={() => handleFulfillmentChange(FulfillmentType.Delivery.Value)}
                      disabled={isCartMutating > 0 || isCartLocked}
                      className="flex items-center gap-2"
                    >
                      <Truck className="h-4 w-4" />
                      {FulfillmentType.Delivery.Name}
                    </Button>
                  </div>
                }
              </div>

              <PersonalizationSection
                cart={cart}
                createCartWithDefaults={createCartWithDefaults}
                disabled={areOrderDetailsLocked}
              />

              {/* Cart Items */}
              <div className="min-h-40">
                {cartEntries.length === 0 ? (
                  <div className="flex items-center justify-center h-full min-h-[200px] text-muted-foreground">
                    <div className="text-center">
                      <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <Typography variant="body">Cart is empty</Typography>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* An order change lists each removed order item crossed out where it stood. */}
                    {cartEntries.map((entry) =>
                      entry.kind === 'item' ? (
                        <CartItemRow
                          key={entry.item.id}
                          item={entry.item}
                          disabled={isCartLocked}
                          quantityByVariantId={quantityByVariantId}
                          onEdit={setEditingCartItem}
                          changeLabel={changeLabelByCartItemId.get(entry.item.id) ?? null}
                        />
                      ) : entry.kind === 'refunded' ? (
                        <RefundedOrderItemRow
                          key={`refunded-${entry.item.sales_order_item_id}`}
                          item={entry.item}
                        />
                      ) : orderChange && cart ? (
                        <RemovedOrderItemRow
                          key={`removed-${entry.row.sales_order_item_id}`}
                          row={entry.row}
                          removed={removedOrderItems}
                          salesOrderId={orderChange.sales_order_id}
                          cartId={cart.id}
                          disabled={isCartLocked}
                        />
                      ) : null
                    )}
                  </div>
                )}
              </div>
            </div>
          </CartItemsScroll>

          <div className={cn('shrink-0', orderChange && 'pr-4 pl-1')}>
            {orderChange && cart ? (
              <div className="shrink-0 space-y-3">
                {/* A change may give or take a discount or change the tip and relay fee; the code tab stays as the order was accepted. */}
                <POSCartDiscount cart={cart} />
                {/* The delivery charge is the one order-level charge a change may move; a refunded one stays as the refund left it. */}
                {quoteFulfillmentType === FulfillmentType.Delivery.Value &&
                !orderChange.delivery_fee_refunded ? (
                  <DeliveryFeeOverrideRow
                    key={[cart.id, receiptDeliveryFee].join(':')}
                    cart={cart}
                    deliveryFee={receiptDeliveryFee}
                    isCartMutating={isCartMutating}
                    isCartSaving={isCartSaving}
                    updateCartMutation={updateCartMutation}
                    showNetworkDeliveryIndicator={showNetworkDeliveryIndicator}
                  />
                ) : null}
                {/* Keyed by cart only: staff choices such as notifying the customer survive each edit. */}
                <PosOrderChangeSummary
                  key={cart.id}
                  cart={cart}
                  onPayNowOpenChange={onOrderChangePayNowOpenChange}
                />
              </div>
            ) : (
              <>
                <POSCartDiscount cart={cart} />

                {/* Total Section */}
                <div className="shrink-0 border-t pt-3">
                  <div className="flex items-center justify-between gap-3">
                    <Typography variant="label" tone="muted" className="min-w-0">
                      Items: {cartItemCount}, Quantity: {cartQuantity}
                    </Typography>
                  </div>

                  <div
                    className="mt-2 grid grid-cols-1 gap-y-1"
                    data-testid="pos-cart-total-breakdown"
                  >
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <Typography variant="label" tone="muted" className="truncate">
                        Subtotal
                      </Typography>
                      {isCartSaving > 0 ? (
                        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
                      ) : (
                        <Typography variant="label" tone="muted" className="shrink-0">
                          {formatKnownCurrency(receiptSubtotal)}
                        </Typography>
                      )}
                    </div>
                    {receiptDiscount !== 0 && (
                      <div className="flex min-w-0 items-center justify-between gap-2">
                        <Typography variant="label" tone="muted" className="truncate">
                          Discount
                        </Typography>
                        {isCartSaving > 0 ? (
                          <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
                        ) : (
                          <Typography variant="label" tone="muted" className="shrink-0">
                            -{formatCurrency(Math.abs(receiptDiscount))}
                          </Typography>
                        )}
                      </div>
                    )}
                    {quoteFulfillmentType === FulfillmentType.Delivery.Value && (
                      <DeliveryFeeOverrideRow
                        key={[cart?.id ?? 'new', receiptDeliveryFee].join(':')}
                        cart={cart}
                        deliveryFee={receiptDeliveryFee}
                        isCartMutating={isCartMutating}
                        isCartSaving={isCartSaving}
                        updateCartMutation={updateCartMutation}
                        showNetworkDeliveryIndicator={showNetworkDeliveryIndicator}
                      />
                    )}
                    {receiptTipAmount > 0 && (
                      <div className="flex min-w-0 items-center justify-between gap-2">
                        <Typography variant="label" tone="muted" className="truncate">
                          Tip
                        </Typography>
                        {isCartSaving > 0 ? (
                          <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
                        ) : (
                          <Typography variant="label" tone="muted" className="shrink-0">
                            {formatCurrency(receiptTipAmount)}
                          </Typography>
                        )}
                      </div>
                    )}
                    {receiptRelayFee > 0 && (
                      <div className="flex min-w-0 items-center justify-between gap-2">
                        <Typography variant="label" tone="muted" className="truncate">
                          Relay Fee
                        </Typography>
                        {isCartSaving > 0 ? (
                          <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
                        ) : (
                          <Typography variant="label" tone="muted" className="shrink-0">
                            {formatCurrency(receiptRelayFee)}
                          </Typography>
                        )}
                      </div>
                    )}
                    <PosCartShopFeeRow cart={cart} />
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <Typography variant="label" tone="muted" className="truncate">
                          Tax
                        </Typography>
                        {cart?.effective_tax_exempt ? (
                          <Badge variant="info" size="xs">
                            Tax exempt
                          </Badge>
                        ) : null}
                      </div>
                      {isCartSaving > 0 ? (
                        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
                      ) : (
                        <Typography variant="label" tone="muted" className="shrink-0">
                          {formatKnownCurrency(displayTax)}
                        </Typography>
                      )}
                    </div>
                    {/* Read top down: the order's total, what's been paid, then what's left, the biggest line. */}
                    {hasCartPayments ? (
                      <>
                        <div className="flex min-w-0 items-center justify-between gap-2">
                          <Typography variant="labelStrong" className="truncate">
                            Total
                          </Typography>
                          {isCartSaving > 0 ? (
                            <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
                          ) : (
                            <Typography variant="labelStrong" className="shrink-0">
                              {formatKnownCurrency(displayTotal)}
                            </Typography>
                          )}
                        </div>
                        <div className="flex min-w-0 items-center justify-between gap-2">
                          <Typography variant="label" tone="muted" className="truncate">
                            Paid
                          </Typography>
                          <Typography variant="label" tone="muted" className="shrink-0">
                            {formatCurrency(displayPaidTotal)}
                          </Typography>
                        </div>
                      </>
                    ) : null}
                    <div className="flex items-center justify-between gap-2">
                      <Typography variant="h4">
                        {hasCartPayments ? 'Remaining' : 'Total'}
                      </Typography>
                      {isCartSaving > 0 ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                      ) : (
                        <Typography variant="h4">
                          {formatKnownCurrency(
                            hasCartPayments ? receiptRemainingTotal : displayTotal
                          )}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment */}
                <div className="mt-3 shrink-0 space-y-2">
                  {canRefundPartialPayments && partialRefundStatusUnavailable ? (
                    <CashDrawerStatusError
                      isRetrying={cashDrawerQueriesAreFetching(
                        cashDrawerStatusQuery,
                        workstationsQuery,
                        currentDrawerQuery
                      )}
                      onRetry={() =>
                        retryFailedCashDrawerQueries(
                          cashDrawerStatusQuery,
                          workstationsQuery,
                          currentDrawerQuery
                        )
                      }
                    />
                  ) : null}
                  {canRefundPartialPayments &&
                  partialRefundRequiresTill &&
                  !isCashDrawerLoading &&
                  !selectedRefundCashDrawer ? (
                    <Typography variant="label" tone="destructive" className="block">
                      {canOperateCashDrawers
                        ? refundTillBlocker
                        : "You don't have permission to refund cash or check payments while cash drawer tracking is on."}
                    </Typography>
                  ) : null}
                  {canRefundPartialPayments &&
                  partialRefundRequiresTill &&
                  selectedRefundCashDrawer &&
                  pairedWorkstation ? (
                    <Typography variant="supporting" className="block">
                      Refund from{' '}
                      {getCashDrawerOptionLabel({
                        workstation: pairedWorkstation,
                        location: pairedWorkstation.location,
                      })}
                    </Typography>
                  ) : null}
                  {canRefundPartialPayments && (
                    <ConfirmationDialog
                      variant="destructive"
                      title="Refund collected payments?"
                      description="This will return the payments collected for this cart to their original payment methods."
                      onConfirm={handleRefundPartialPayments}
                      isPending={
                        refundPartialPaymentsMutation.isPending || isCashDrawerAdmissionPending
                      }
                      confirmDisabled={
                        partialRefundStatusUnavailable || isCashDrawerAdmissionPending
                      }
                      pendingText="Refunding..."
                      confirmText="Refund Payments"
                      trigger={
                        <Button
                          variant="destructive"
                          className="w-full"
                          disabled={
                            refundPartialPaymentsMutation.isPending ||
                            isCashDrawerAdmissionPending ||
                            isCartMutating > 0 ||
                            isCashDrawerLoading ||
                            partialRefundStatusUnavailable
                          }
                        >
                          Refund Payments
                        </Button>
                      }
                    />
                  )}

                  <div
                    className="grid grid-cols-1 @[30rem]:grid-cols-2 gap-2"
                    data-testid="pos-payment-actions"
                  >
                    <Button
                      variant="success"
                      className="flex w-full items-center gap-2"
                      onPointerEnter={() =>
                        !canCompleteOrder &&
                        void preloadPaymentModal({ paymentState: cart }).catch(() => {})
                      }
                      onFocus={() =>
                        !canCompleteOrder &&
                        void preloadPaymentModal({ paymentState: cart }).catch(() => {})
                      }
                      onClick={
                        canCompleteOrder
                          ? () => handleFinalizeSalesOrder('pay_now')
                          : openPaymentModal
                      }
                      disabled={
                        isPaymentModalOpening ||
                        !cart ||
                        isCartMutating > 0 ||
                        finalizeCartSalesOrderMutation.isPending ||
                        (!canCompleteOrder && !isPaymentModalReachable) ||
                        (!canCompleteOrder &&
                          !isPreparedPosPaymentSessionLocked &&
                          !hasPaymentToPickUp &&
                          (cartItemCount === 0 ||
                            availableSplitRemainingTotal <= 0 ||
                            Boolean(subscriptionPaymentBlocker) ||
                            hasInvalidRelayFee))
                      }
                    >
                      {isPaymentModalOpening ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="h-4 w-4" />
                      )}
                      {finalizeCartSalesOrderMutation.isPending
                        ? 'Completing Order…'
                        : canCompleteOrder
                          ? 'Complete Order'
                          : isPaymentModalOpening
                            ? 'Checking printer…'
                            : 'Pay Now'}
                    </Button>
                    <PayLaterConfirmationDialog
                      title="Create pay-later order?"
                      description="Review what will be left unpaid before creating the order."
                      customer={cart?.purchaser}
                      unpaidAmount={payLaterBalance}
                      projection={payLaterProjection}
                      onConfirm={() => handleFinalizeSalesOrder('pay_later')}
                      onOpenChange={setIsPayLaterConfirmationOpen}
                      open={isPayLaterConfirmationOpen}
                      isPending={finalizeCartSalesOrderMutation.isPending}
                      pendingText="Creating Order..."
                      confirmText="Create Order"
                      trigger={
                        payLaterUnavailableReason ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block w-full">{payLaterButton}</span>
                            </TooltipTrigger>
                            <TooltipContent>{payLaterUnavailableReason}</TooltipContent>
                          </Tooltip>
                        ) : (
                          payLaterButton
                        )
                      }
                    />
                  </div>
                  {!isCartLocked &&
                    (cart?.cart_items || []).length > 0 &&
                    (subscriptionPaymentBlocker || (hasSavedCustomer && payLaterHelperText)) && (
                      <Typography variant="label" tone="muted">
                        {subscriptionPaymentBlocker ?? payLaterHelperText}
                      </Typography>
                    )}
                </div>
              </>
            )}
          </div>
        </CartColumnScroll>
      </CardContent>

      <PickupModal
        key={`pickup-${cart?.id ?? 'new'}-${cart?.updated_at ?? '0'}`}
        open={isPickupModalOpen}
        onOpenChange={setIsPickupModalOpen}
        location={fulfillmentLocation}
        onConfirm={handlePickupConfirm}
        cart={cart}
      />

      <DeliveryModal
        key={deliveryModalKey}
        open={isDeliveryModalOpen}
        onOpenChange={handleDeliveryModalOpenChange}
        location={fulfillmentLocation}
        onConfirm={handleDeliveryConfirm}
        cart={cart}
        cartId={deliveryModalCartId}
        createCartWithDefaults={createCartWithDefaults}
      />

      {paymentModalMode ? (
        <PaymentModal
          fallback={({ error, retry }) => (
            <PaymentModalLoadFallback
              error={error}
              open={Boolean(paymentModalMode)}
              onCancel={cancelPaymentModalLoad}
              onRetry={retry}
            />
          )}
          mode={paymentModalMode}
          onInitializationStage={recordPaymentModalInitializationStage}
          onModeChange={setPaymentModalMode}
          cart={cart}
          paymentTarget={{ kind: 'cart', cartId: cart?.id ?? null }}
          // oxlint-disable-next-line react/refs -- grandfathered
          organizationWorkstationId={posPrintWorkstationIdRef.current}
          // oxlint-disable-next-line react/refs -- grandfathered
          organizationWorkstationClaim={posPrintWorkstationClaimRef.current}
          onCartSalesOrderComplete={handleCompletedSalesOrder}
          onCartSalesOrderReadyForOutput={handleCompletedSalesOrderOutput}
          onSalesOrderNavigationStart={onSalesOrderNavigationStart}
          onSalesOrderNavigationEnd={onSalesOrderNavigationEnd}
          onNavigationBlockingChange={onPaymentNavigationBlockingChange}
        />
      ) : null}
      <CartItemDetailsModal
        open={Boolean(editingCartItem)}
        onOpenChange={(isOpen: boolean) => {
          if (!isOpen) {
            setEditingCartItem(null)
          }
        }}
        cart={cart}
        cartId={cart?.id ?? editingCartItem?.cart_id ?? null}
        createCartWithDefaults={createCartWithDefaults}
        disabled={isCartLocked}
        cartItem={editingCartItem}
        initialValues={
          editingCartItem ? getItemDetailsModalValuesFromCartItem(editingCartItem) : null
        }
        focusPriceOnOpen
      />
      <PosOrderCompletionFlow
        key={completedSalesOrderId ?? 'no-completed-sales-order'}
        salesOrderId={completedSalesOrderId}
        organizationId={completedSalesOrderOrgId}
        canSendToFsn={canSendCompletedSalesOrderToFsn}
        requiresFsnCardDetails={completedSalesOrderRequiresFsnCardDetails}
        isViewingOrder={isViewingCompletedSalesOrder}
        onStartNewOrder={handleStartNewOrderAfterCompletion}
        onViewOrder={handleViewCompletedSalesOrder}
      />
    </Card>
  )
}
